'use client';

import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { color, spacing, breakpoints } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import { TextFieldFilledWhite } from '@/components/formElements/textFields/TextFieldFilledWhite';
import ButtonContainedBrand from '@/components/formElements/buttons/ButtonContainedBrand';
import { useRouter } from '@/i18n/routing';
import SectionWrapper from '@/components/common/SectionWrapper';


const LeftStack = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: ${spacing[80]};
    max-width: 500px;
    
    @media (max-width: ${breakpoints.sm}) {
        max-width: 100%;
        gap: ${spacing[48]};
    }
    
    @media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) {
        gap: ${spacing[64]};
    }
`;

const TitleStack = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: ${spacing[12]};
`;

const TrackStack = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: ${spacing[16]};
`;

const TrackRow = styled(Box)`
    display: flex;
    gap: ${spacing[8]};
`;

const Location = styled(Box)`
    position: absolute;
    bottom: ${spacing[16]};
    right: 0;
    color: ${color.white[0]};
    text-align: right;
    
    @media (max-width: ${breakpoints.sm}) {
        display: none;
    }
`;


export default function HeroHeader() {
    const t = useTranslations('HomePage.hero');
    const [query, setQuery] = useState('');
    const router = useRouter();

    const handleSearch = useCallback(() => {
        router.push(`/cargo${query ? `?q=${encodeURIComponent(query)}` : ''}`);
    }, [router, query]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    }, [handleSearch]);

    return (
        <SectionWrapper
            alignItems="flex-end"
            contentAlignItems="flex-end"
            showGradientOverlay
            backgroundImage="/homepage/homepage-header-desktop.jpg"
            backgroundImageMobile="/homepage/homepage-header-mobile.jpg"
            backgroundImageTablet="/homepage/homepage-header-tablet.jpg"
            priority
        >
            <Box sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: spacing[16],
                marginTop: {
                    xs: '75px',
                    sm: '263px',
                    md: '204px'
                },
                marginBottom: {
                    xs: spacing[16],
                    sm: spacing[16],
                    md: spacing[16]
                }
            }}>
                <LeftStack>
                    <TitleStack>
                        <Typography component="h1" variant="heading6bold" color={color.white[0]}>
                            {t('title')}
                        </Typography>
                        <Typography component="p" variant="paragraph5regular" color={color.white[0]}>
                            {t('subtitle')}
                        </Typography>
                    </TitleStack>

                    <TrackStack>
                        <Typography component="h2" variant="heading4bold" color={color.white[0]}>
                            {t('trackLabel')}
                        </Typography>
                        <Box>
                            <TrackRow>
                                <TextFieldFilledWhite
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                    label={t('searchPlaceholder')}
                                    type="input"
                                    onSearchClick={handleSearch}
                                    onKeyDown={handleKeyDown}
                                />
                                <ButtonContainedBrand onClick={handleSearch} sx={{ whiteSpace: 'nowrap' }}>
                                    <Typography variant="label5medium" color={color.white[0]}>
                                        {t('buttonLabel')}
                                    </Typography>
                                </ButtonContainedBrand>
                            </TrackRow>
                            <Box sx={{ marginTop: spacing[4], marginLeft: spacing[12] }}>
                                <Typography variant="label2regular" color={color.white[0]}>
                                    {t('helperText')}
                                </Typography>
                            </Box>
                        </Box>
                    </TrackStack>
                </LeftStack>

                <Location>
                    <Typography variant="label5regular">
                        {t('locationLine1')}
                        <br />
                        {t('locationLine2')}
                    </Typography>
                </Location>
            </Box>
        </SectionWrapper>
    );
}




